import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { ReportShare } from '@/hooks/useReportShares';

interface ReportShareDialogState {
  isOpen: boolean;
  editingShare: ReportShare | null;
  onSuccessCallback?: () => void;
}

interface ReportShareDialogContextType {
  state: ReportShareDialogState;
  openCreateDialog: (onSuccess?: () => void) => void;
  openEditDialog: (share: ReportShare, onSuccess?: () => void) => void;
  closeDialog: () => void;
}

const ReportShareDialogContext = createContext<ReportShareDialogContextType | undefined>(undefined);

export function ReportShareDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ReportShareDialogState>({
    isOpen: false,
    editingShare: null,
    onSuccessCallback: undefined,
  });

  const openCreateDialog = useCallback((onSuccess?: () => void) => {
    setState({
      isOpen: true,
      editingShare: null,
      onSuccessCallback: onSuccess,
    });
  }, []);

  const openEditDialog = useCallback((share: ReportShare, onSuccess?: () => void) => {
    setState({
      isOpen: true,
      editingShare: share,
      onSuccessCallback: onSuccess,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setState({
      isOpen: false,
      editingShare: null,
      onSuccessCallback: undefined,
    });
  }, []);

  return (
    <ReportShareDialogContext.Provider value={{ state, openCreateDialog, openEditDialog, closeDialog }}>
      {children}
    </ReportShareDialogContext.Provider>
  );
}

export function useReportShareDialog() {
  const context = useContext(ReportShareDialogContext);
  if (!context) {
    throw new Error('useReportShareDialog must be used within a ReportShareDialogProvider');
  }
  return context;
}
